import { useEffect, useState, useRef, useMemo } from 'react';
import { Loader2, Terminal, FileText, CheckCircle2, Pencil, Search, Play, File, Clock, Volume2 } from 'lucide-react';
import './AdvancedModeProcessLoaderComponent.scss';

// Catchy loading texts for advanced mode
const LOADING_TEXTS = [
    "Analyzing your request...",
    "Understanding the codebase...",
    "Planning the best approach...",
    "Preparing to make changes...",
    "Setting up the environment...",
    "Connecting to AI agent...",
    "Gathering context...",
    "Processing your instructions..."
];

export type ProcessEvent =
    | 'initializing'
    | 'analyzing'
    | 'planning'
    | 'executing'
    | 'generating'
    | 'saving'
    | 'complete';

export type ActionType = 'read' | 'write' | 'new' | 'edit' | 'execute' | 'search' | 'other';

export interface ProcessItem {
    id: string;
    type: 'message' | 'tool-call';
    content: string;
    toolName?: string;
    status: 'streaming' | 'completed';
    timestamp: number;
    is_completed?: boolean;
    total_cost_usd?: number;
    model_usage?: {
        input_tokens: number;
        output_tokens: number;
        total_tokens: number;
    };
}

interface AdvancedModeProcessLoaderComponentProps {
    items?: ProcessItem[];
    className?: string;
}

// Helper function to determine action type from tool name
const getActionType = (toolName?: string): ActionType => {
    if (!toolName) return 'other';

    const lowerName = toolName.toLowerCase();

    // Read operations
    if (lowerName.includes('read') || lowerName.includes('glob') || lowerName.includes('grep')) {
        return 'read';
    }

    // Write operations
    if (lowerName.includes('write')) {
        return 'write';
    }

    // New/Create operations
    if (lowerName.includes('create') || lowerName.includes('new')) {
        return 'new';
    }

    // Edit operations
    if (lowerName.includes('edit')) {
        return 'edit';
    }

    // Execute operations
    if (lowerName.includes('bash') || lowerName.includes('exec') || lowerName.includes('kill')) {
        return 'execute';
    }

    // Search operations
    if (lowerName.includes('search') || lowerName.includes('fetch')) {
        return 'search';
    }

    return 'other';
};

// Helper function to get icon based on action type
const getToolIcon = (toolName?: string) => {
    const actionType = getActionType(toolName);

    switch (actionType) {
        case 'read':
            return <FileText className="tool-icon" size={14} style={{ color: '#9bdcff' }} />;
        case 'write':
            return <File className="tool-icon" size={14} style={{ color: '#ffb74d' }} />;
        case 'new':
            return <File className="tool-icon" size={14} style={{ color: '#66ff99' }} />;
        case 'edit':
            return <Pencil className="tool-icon" size={14} style={{ color: '#ffb74d' }} />;
        case 'execute':
            return <Terminal className="tool-icon" size={14} style={{ color: '#ba93ff' }} />;
        case 'search':
            return <Search className="tool-icon" size={14} style={{ color: '#9bdcff' }} />;
        default:
            return <Play className="tool-icon" size={14} style={{ color: '#9d9fa0' }} />;
    }
};

// Helper function to extract filename from content (e.g., "Reading /path/to/file.tsx" -> "file.tsx")
const extractFilename = (content?: string): string | null => {
    if (!content) return null;

    // Match file paths in the content
    const pathMatch = content.match(/(?:Reading|Writing|Editing|Executing)\s+(.+?)(?:\s|$)/i);
    if (pathMatch && pathMatch[1]) {
        const fullPath = pathMatch[1];
        // Extract just the filename from the path
        const filename = fullPath.split('/').pop();
        return filename || null;
    }

    // Try to match any file path pattern
    const filePathMatch = content.match(/\/[^\s]+\/([^\s/]+\.[a-zA-Z]+)/);
    if (filePathMatch && filePathMatch[1]) {
        return filePathMatch[1];
    }

    return null;
};

// Action badge component with optional filename
const ActionBadge = ({ toolName, content }: { toolName?: string; content?: string }) => {
    const actionType = getActionType(toolName);

    if (actionType === 'other') return null;

    const labels: Record<ActionType, string> = {
        read: 'Read',
        write: 'Write',
        new: 'New',
        edit: 'Edit',
        execute: 'Exec',
        search: 'Search',
        other: ''
    };

    const filename = extractFilename(content);
    const displayText = filename ? `${labels[actionType]}: ${filename}` : labels[actionType];

    return (
        <span className={`tool-action-badge ${actionType}`}>
            {displayText}
        </span>
    );
};

const AdvancedModeProcessLoaderComponent = ({
    items = [],
    className
}: AdvancedModeProcessLoaderComponentProps) => {
    const [displayItems, setDisplayItems] = useState<ProcessItem[]>([]);
    const [loadingTextIndex, setLoadingTextIndex] = useState(0);
    const previousItemIdsRef = useRef<Set<string>>(new Set());

    // Rotate loading texts when no items are available
    useEffect(() => {
        if (displayItems.length === 0) {
            const interval = setInterval(() => {
                setLoadingTextIndex(prev => (prev + 1) % LOADING_TEXTS.length);
            }, 2500);
            return () => clearInterval(interval);
        }
    }, [displayItems.length]);

    const currentLoadingText = useMemo(() => {
        return LOADING_TEXTS[loadingTextIndex];
    }, [loadingTextIndex]);

    useEffect(() => {
        if (!items || items.length === 0) {
            setDisplayItems([]);
            previousItemIdsRef.current = new Set();
            return;
        }

        let hasChanges = false;

        setDisplayItems(prevItems => {
            const itemMap = new Map<string, ProcessItem>();
            const orderMap = new Map<string, number>();

            prevItems.forEach((item, index) => {
                itemMap.set(item.id, item);
                orderMap.set(item.id, index);
            });

            items.forEach(item => {
                const existing = itemMap.get(item.id);
                if (existing) {
                    if (existing.content !== item.content || existing.status !== item.status) {
                        itemMap.set(item.id, {
                            ...existing,
                            content: item.content,
                            status: item.status || existing.status,
                            toolName: item.toolName || existing.toolName,
                            timestamp: existing.timestamp
                        });
                        hasChanges = true;
                    }
                } else {
                    const newIndex = itemMap.size;
                    itemMap.set(item.id, item);
                    orderMap.set(item.id, newIndex);
                    hasChanges = true;
                }
            });

            if (!hasChanges) {
                return prevItems;
            }

            previousItemIdsRef.current = new Set(itemMap.keys());

            return Array.from(itemMap.values()).sort((a, b) => {
                const orderA = orderMap.get(a.id) ?? Infinity;
                const orderB = orderMap.get(b.id) ?? Infinity;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.timestamp - b.timestamp;
            });
        });
    }, [items]);

    const lastEvent = displayItems[displayItems.length - 1];
    const isCompleted = lastEvent?.is_completed;
    const currentItem = displayItems[displayItems.length - 1];
    const completedSteps = displayItems.filter(item => item.status === 'completed').length;

    // If completed, show nothing (replaced by FileListStreaming action card)
    if (isCompleted) {
        return null;
    }

    return (
        <>
            <div className={`advanced-mode-process-loader ${className || ''} processing`}>
                <div className="process-loader-header">
                    <div className="header-left">
                        <span className="header-title">
                            <Loader2 className="header-spinner" size={12} />
                            Agent Activity
                        </span>
                    </div>
                    <span className="header-chip processing">
                        {displayItems.length > 0 ? `${displayItems.length} ${displayItems.length === 1 ? 'step' : 'steps'}` : 'Processing'}
                    </span>
                </div>
                <div className="process-content">
                    {currentItem ? (
                        <div className="current-step">
                            {currentItem.type === 'message' ? (
                                <>
                                    <Loader2 className="step-icon spinning" size={16} />
                                    <span className="step-text">
                                        {currentItem.content || 'Thinking...'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    {currentItem.status === 'streaming' ? (
                                        <Loader2 className="step-icon spinning" size={16} />
                                    ) : (
                                        <CheckCircle2 className="step-icon completed" size={16} />
                                    )}
                                    {getToolIcon(currentItem.toolName)}
                                    <span className="step-text">
                                        {currentItem.toolName || 'Processing...'}
                                    </span>
                                    {currentItem.status === 'streaming' && (
                                        <ActionBadge toolName={currentItem.toolName} content={currentItem.content} />
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="current-step">
                            <Loader2 className="step-icon spinning" size={16} />
                            <span className="step-text">{currentLoadingText}</span>
                        </div>
                    )}
                    {displayItems.length > 0 && (
                        <div className="step-progress">
                            {currentItem?.status === 'streaming'
                                ? `Processing step ${displayItems.length}...`
                                : completedSteps > 0
                                    ? `${completedSteps} step${completedSteps === 1 ? '' : 's'} completed`
                                    : ''}
                        </div>
                    )}
                </div>
            </div>

            <div className="advanced-mode-waiting-banner">
                <div className="banner-icon-wrapper">
                    <Clock size={12} className="banner-icon" />
                </div>
                <div className="banner-content">
                    <span className="banner-text">
                        Larger tasks might take up to 5-10 mins. Don't close this tab. A sound will play once done. Good things take time!
                    </span>
                </div>
                <div className="banner-icon-wrapper">
                    <Volume2 size={12} className="banner-icon-right" />
                </div>
            </div>
        </>
    );
};

export default AdvancedModeProcessLoaderComponent;
